import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  GridApi,
  IDoesFilterPassParams,
  IRowNode,
  ISetFilterParams,
  SetFilterValuesFuncParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import type { ActiveGroupings } from './multiModalColumnFilter.utilities';
import {
  BLANK_LABEL,
  collectCapsuleValues,
  collectDefaultValues,
  collectGroupedValues,
  createValueCollector,
  extractSelectionSet,
  FilterMode,
  type FilterModel,
  type FilterPrimitive,
  type FilterValue,
  finalizeCollectedValues,
  getActiveGroupings,
  getAssetColId,
  getConditionColId,
  matchesSelected,
  matchGroupedSelection,
  modelToSerializable,
} from './multiModalColumnFilter.utilities';
import type { Maybe } from '@/utilities.types';
import _ from 'lodash';

interface FilterStateConfig {
  model: Maybe<FilterModel>;
  onModelChange: (model: FilterModel | null) => void;
}

interface FilterStateResult {
  filterMode: FilterMode;
  selectedValues: Set<FilterPrimitive>;
  setFilterMode: (mode: FilterMode) => void;
  setSelectedValues: (values: Set<FilterPrimitive>) => void;
  updateModel: (mode: FilterMode, sel: Set<FilterPrimitive>) => void;
  handleFilterModeChange: (mode: FilterMode) => void;
  handleValueToggle: (value: FilterPrimitive) => void;
  handleClear: () => void;
  handleSelectAll: (availableValues: FilterPrimitive[]) => void;
}

/**
 * Hook for managing filter mode and selected values state
 */
export const useFilterState = ({ model, onModelChange }: FilterStateConfig): FilterStateResult => {
  const [filterMode, setFilterMode] = useState<FilterMode>(model?.filterMode || FilterMode.Default);
  const [selectedValues, setSelectedValues] = useState<Set<FilterPrimitive>>(() => extractSelectionSet(model));
  // Keep track of selections per mode so switching modes preserves selections
  const selectionsByModeRef = useRef<Map<FilterMode, Set<FilterPrimitive>>>(new Map());
  // Track if we're making an internal update to avoid overwriting state
  const isInternalUpdateRef = useRef(false);

  // Sync state with model changes (e.g., when loading a saved view)
  // Only sync if this is an external change, not from our own internal updates
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    if (model) {
      const newMode = model.filterMode;
      const newSelections = extractSelectionSet(model);

      setFilterMode(newMode);
      setSelectedValues(newSelections);
      selectionsByModeRef.current.set(newMode, new Set(newSelections));
    } else {
      // No filter - reset to defaults
      setFilterMode(FilterMode.Default);
      setSelectedValues(new Set());
      selectionsByModeRef.current.clear();
    }
  }, [model]);

  const updateModel = (mode: FilterMode, selected: Set<FilterPrimitive>) => {
    isInternalUpdateRef.current = true;
    const serializable = modelToSerializable(mode, selected);
    onModelChange(serializable);
  };

  const handleFilterModeChange = (mode: FilterMode) => {
    // Save current selections for the current mode
    selectionsByModeRef.current.set(filterMode, new Set(selectedValues));

    // Restore selections for the new mode, or use empty set if none exist
    const restoredSelections = selectionsByModeRef.current.get(mode) || new Set<FilterPrimitive>();

    setFilterMode(mode);
    setSelectedValues(restoredSelections);
    updateModel(mode, restoredSelections);
  };

  const handleValueToggle = (value: FilterPrimitive) => {
    const next = new Set(selectedValues);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    setSelectedValues(next);
    selectionsByModeRef.current.set(filterMode, new Set(next));
    updateModel(filterMode, next);
  };

  const handleClear = () => {
    const cleared = new Set<FilterPrimitive>();
    setSelectedValues(cleared);
    setFilterMode(FilterMode.Default);
    selectionsByModeRef.current.clear();
    updateModel(FilterMode.Default, cleared);
  };

  const handleSelectAll = (availableValues: FilterPrimitive[]) => {
    const all = new Set(availableValues);
    setSelectedValues(all);
    selectionsByModeRef.current.set(filterMode, new Set(all));
    updateModel(filterMode, all);
  };

  return {
    filterMode,
    selectedValues,
    setFilterMode,
    setSelectedValues,
    updateModel,
    handleFilterModeChange,
    handleValueToggle,
    handleClear,
    handleSelectAll,
  };
};

/**
 * Hook for detecting which groupings are currently active
 */
export const useActiveGroupings = (api: GridApi): ActiveGroupings => {
  const [groupings, setGroupings] = useState<ActiveGroupings>({
    hasConditionGrouping: false,
    hasAssetGrouping: false,
  });

  useEffect(() => {
    const onColumnRowGroupChanged = () => {
      setGroupings(getActiveGroupings(api));
    };

    setGroupings(getActiveGroupings(api));
    api.addEventListener('columnRowGroupChanged', onColumnRowGroupChanged);

    return () => {
      api.removeEventListener('columnRowGroupChanged', onColumnRowGroupChanged);
    };
  }, [api]);

  return groupings;
};

interface AvailableValuesConfig {
  api: GridApi;
  field: string;
  filterMode: FilterMode;
  getValueForNode: (node: IRowNode, useAggData?: boolean) => FilterValue;
  filterParams: ISetFilterParams;
}

/**
 * Hook for fetching available values based on the current filter mode
 */
export const useAvailableValues = ({
  api,
  field,
  filterMode,
  getValueForNode,
  filterParams,
}: AvailableValuesConfig): FilterPrimitive[] => {
  const [availableValues, setAvailableValues] = useState<FilterPrimitive[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const filterParamsRef = useRef(filterParams);

  useEffect(() => {
    filterParamsRef.current = filterParams;
  }, [filterParams]);

  // Listen for grid data changes and trigger refresh
  useEffect(() => {
    const handleDataChange = () => {
      setRefreshTrigger((prev) => prev + 1);
    };

    // Listen to events that indicate data has changed
    api.addEventListener('cellValueChanged', handleDataChange);
    api.addEventListener('rowDataUpdated', handleDataChange);
    api.addEventListener('modelUpdated', handleDataChange);

    return () => {
      api.removeEventListener('cellValueChanged', handleDataChange);
      api.removeEventListener('rowDataUpdated', handleDataChange);
      api.removeEventListener('modelUpdated', handleDataChange);
    };
  }, [api]);

  useEffect(() => {
    let isMounted = true;

    const fetchAvailableValues = () => {
      const collector = createValueCollector();
      const filterParam = filterParamsRef.current;

      // Check if this column has a function provider - if so, always include blanks
      const hasFunctionProvider = _.isFunction(filterParam?.values);

      const finalizeAndSet = (options?: { seedValues?: FilterPrimitive[] }) => {
        const values = finalizeCollectedValues({
          collector,
          seedValues: options?.seedValues,
          filterMode,
          field,
          ensureBlanks: hasFunctionProvider,
        });

        if (isMounted) {
          setAvailableValues(values);
        }
      };

      if (filterMode === FilterMode.Default) {
        if (filterParam?.values) {
          if (_.isFunction(filterParam?.values)) {
            const column = api.getColumn(field);
            if (!column) {
              collectDefaultValues(api, getValueForNode, collector);
              finalizeAndSet();
              return;
            }

            const colDef = column.getColDef();
            const params: SetFilterValuesFuncParams = {
              success: (values: FilterPrimitive[]) => {
                collectDefaultValues(api, getValueForNode, collector);
                finalizeAndSet({ seedValues: values });
              },
              column,
              api,
              colDef,
              context: {},
            };

            filterParam.values(params);
            return;
          } else {
            // When values is a provided array use them directly, don't collect from data
            // This ensures columns like Suppressed/Flagged always show all discrete values
            finalizeAndSet({ seedValues: filterParam.values as FilterPrimitive[] });
            return;
          }
        }
        collectDefaultValues(api, getValueForNode, collector);
        finalizeAndSet();
        return;
      }

      switch (filterMode) {
        case FilterMode.Capsule:
          collectCapsuleValues(api, getValueForNode, collector);
          break;
        case FilterMode.Condition:
          collectGroupedValues(api, getConditionColId(api), getValueForNode, collector);
          break;
        case FilterMode.Asset:
          collectGroupedValues(api, getAssetColId(api), getValueForNode, collector);
          break;
        default:
          collectDefaultValues(api, getValueForNode, collector);
          break;
      }
      // If discrete values are provided, seed with those
      const hasProvider = !_.isNil(filterParam?.values);
      const hasArrayProvider = hasProvider && !hasFunctionProvider;
      const seedValues = hasArrayProvider ? (filterParam.values as FilterPrimitive[]) : undefined;

      finalizeAndSet({ seedValues });
    };

    fetchAvailableValues();

    return () => {
      isMounted = false;
    };
  }, [filterMode, api, field, getValueForNode, refreshTrigger]);

  return availableValues;
};

interface FilterPassConfig {
  model: Maybe<FilterModel>;
  api: GridApi;
  field: string;
  getValueForNode: (node: IRowNode, useAggData?: boolean) => FilterValue;
}

/**
 * Hook for the core filtering logic
 */
export const useFilterPass = ({
  model,
  api,
  field,
  getValueForNode,
}: FilterPassConfig): ((params: IDoesFilterPassParams) => boolean) => {
  return useCallback(
    ({ node }: IDoesFilterPassParams): boolean => {
      if (!model) {
        return true;
      }

      const modelSelection = extractSelectionSet(model);

      if (modelSelection.size === 0) {
        return true;
      }

      switch (model.filterMode) {
        case FilterMode.Capsule: {
          if (node.group) {
            return false;
          }
          const val = getValueForNode(node, false);
          return matchesSelected(val, modelSelection);
        }

        case FilterMode.Condition: {
          const conditionColId = getConditionColId(api);
          return matchGroupedSelection(node, conditionColId, modelSelection, getValueForNode);
        }
        case FilterMode.Asset: {
          const assetColId = getAssetColId(api);
          return matchGroupedSelection(node, assetColId, modelSelection, getValueForNode);
        }

        case FilterMode.Default:
        default: {
          const val = getValueForNode(node, !!node.group);
          return matchesSelected(val, modelSelection);
        }
      }
    },
    [model, field, api, getValueForNode],
  );
};

interface ModelSerializerConfig {
  api: GridApi;
  field: string;
  filterParams: ISetFilterParams;
  getActiveGroupings: () => ActiveGroupings;
}

/**
 * Hook for model serialization and deserialization
 */
export const useModelSerializer = ({ api, field, filterParams, getActiveGroupings }: ModelSerializerConfig) => {
  const filterParamsRef = useRef(filterParams);

  useEffect(() => {
    filterParamsRef.current = filterParams;
  }, [filterParams]);

  const afterGuiAttached = useCallback(() => {
    // This is called by AG Grid when the filter is attached
    // State management is handled by useFilterState hook
    getActiveGroupings();
  }, [getActiveGroupings]);

  const getModelAsString = useCallback(
    (m: FilterModel) => {
      const raw = m.selectedValues;
      const asSet = raw instanceof Set ? raw : new Set(Array.isArray(raw) ? raw : []);

      const formatValue = (value: FilterPrimitive) => {
        if (_.isNil(value)) {
          return BLANK_LABEL;
        }

        const current = filterParamsRef.current;
        if (current?.valueFormatter) {
          const column = api.getColumn(field);
          if (column) {
            const colDef = column.getColDef();
            const params: ValueFormatterParams = {
              value,
              api,
              colDef,
              column,
              context: {},
              node: null as any,
              data: null,
            };
            return current.valueFormatter(params);
          }
        }

        const column = api.getColumn(field);
        if (column) {
          const columnDef = column.getColDef();
          const fp = columnDef?.filterParams as
            | { valueFormatter?: (params: ValueFormatterParams) => string }
            | undefined;
          if (fp?.valueFormatter) {
            const params: ValueFormatterParams = {
              value,
              api,
              colDef: columnDef,
              column,
              context: {},
              node: null as any,
              data: null,
            };
            return fp.valueFormatter(params);
          }
        }

        return value?.toString ? value.toString() : String(value ?? '');
      };

      const formattedValues = Array.from(asSet).map((v) => formatValue(v));
      const mode = m.filterMode || FilterMode.Default;
      return `${mode}: ${formattedValues.join(', ')}`;
    },
    [api, field],
  );

  return { afterGuiAttached, getModelAsString };
};

interface ValueFormatterConfig {
  api: GridApi;
  field: string;
  filterParams: ISetFilterParams;
}

/**
 * Hook for value formatting
 */
export const useValueFormatter = ({ api, field, filterParams }: ValueFormatterConfig) => {
  const filterParamsRef = useRef(filterParams);

  useEffect(() => {
    filterParamsRef.current = filterParams;
  }, [filterParams]);

  return (value: FilterPrimitive): string => {
    if (_.isNil(value)) {
      return BLANK_LABEL;
    }

    // Prefer valueFormatter from filter params
    const current = filterParamsRef.current;
    if (current?.valueFormatter) {
      const column = api.getColumn(field);
      if (column) {
        const colDef = column.getColDef();
        const params: ValueFormatterParams = {
          value,
          api,
          colDef,
          column,
          context: {},
          node: null as any,
          data: null,
        };
        return current.valueFormatter(params);
      }
    }

    // Fallback to columnDef.filterParams.valueFormatter if present
    const column = api.getColumn(field);
    if (column) {
      const columnDef = column.getColDef();
      const fp = columnDef?.filterParams as { valueFormatter?: (params: ValueFormatterParams) => string } | undefined;
      if (fp?.valueFormatter) {
        const params: ValueFormatterParams = {
          value,
          api,
          colDef: columnDef,
          column,
          context: {},
          node: null as any,
          data: null,
        };
        return fp.valueFormatter(params);
      }
    }

    return value?.toString ? value.toString() : String(value ?? '');
  };
};

interface ModeDisabledConfig {
  mode: FilterMode;
  groupings: ActiveGroupings;
}

/**
 * Utility to check if a filter mode should be disabled
 */
export const isModeDisabled = ({ mode, groupings }: ModeDisabledConfig): boolean => {
  switch (mode) {
    case FilterMode.Condition:
      return !groupings.hasConditionGrouping;
    case FilterMode.Asset:
      return !groupings.hasAssetGrouping;
    default:
      return false;
  }
};
